import { logger } from '@springtree/eva-sdk-core-logger';
import { IEvaServiceCallOptions, bootstrapEndpoint } from '@springtree/eva-sdk-core-service';
import { Core, IEvaServiceDefinition } from '@springtree/eva-services-core';
// tslint:disable-next-line: import-name
import ky from 'ky-universal';
import { createLogic } from 'redux-logic';
import { IEvaReduxStoreDefaultShape } from '../store';
import {
  IEvaServiceErrorActionPayload,
  IEvaServiceRequestAction,
  IEvaServiceRequestActionPayload,
  IEvaServiceResponseActionPayload,
  IStateOptions,
} from '../action';
import { getServicePromise } from '../promise';
import { getStoreSettings } from '../settings';
import { determineOrderStatus, IOrderStatus } from './order-status';
import { countOrderProductsByType, IProductCountByType } from './product-count-by-type';

/**
 * The order with status requests.
 * The default required for level is set to 7 which are combined requirements for:
 * - Payment
 * - PlaceOrder
 * - ShipOrder
 *
 * @export
 * @interface IGetOrderWithStatusRequest
 */
export interface IGetOrderWithStatusRequest {
  order: EVA.Core.GetOrder;
}

/**
 * Combined response of the order with its required data.
 * Will also contain checkout (step) status and product counts
 *
 * @export
 * @interface IGetOrderWithStatusResponse
 */
export interface IGetOrderWithStatusResponse {
  order: EVA.Core.GetOrderResponse;
  status: IOrderStatus;
  productCount: IProductCountByType;
}

/**
 * The composite service definition for the GetOrderWithStatus helper
 *
 * @export
 * @class GetOrderWithStatus
 * @implements {IEvaServiceDefinition}
 */
export class GetOrderWithStatus implements IEvaServiceDefinition {
  name = 'Composite:GetOrderWithStatus';
  path = '/api/composite/GetOrderWithStatus';
  request?: IGetOrderWithStatusRequest;
  response?: IGetOrderWithStatusResponse;
}

/**
 * Composite service request logic builder for the GetOrderWithStatus helper
 *
 * @export
 * @template SHAPE
 * @param {IGetOrderWithStatusLogicOptions} [stateOptions]
 * @returns
 */
export function createGetOrderWithStatusLogic<SHAPE extends IEvaReduxStoreDefaultShape>(
  stateOptions?: IStateOptions,
) {

  // Determine the state key name either by the service name or the provided
  // custom state key name
  //
  const serviceName = (stateOptions && stateOptions.stateKey) || new GetOrderWithStatus().name;

  const requestLogic = createLogic<SHAPE, IEvaServiceRequestAction<GetOrderWithStatus>, any, any, any, string>({
    type: `${serviceName}:REQUEST`,
    processOptions: {
      dispatchReturn: true,
      successType: `${serviceName}:RESPONSE`,
      failType: `${serviceName}:ERROR`,
    },
    async process({ getState, action }) {
      const storeState: SHAPE = getState();
      const settings = getStoreSettings();
      const payload: IEvaServiceRequestActionPayload<GetOrderWithStatus> = action.payload;
      const endpointUrl = payload.endpoint || storeState.endpointUrl;
      const servicePromise = getServicePromise(payload.serviceContext);

      // Ensure we have an endpoint to use
      //
      if (!endpointUrl) {
        const failure = new Error('No EVA endpoint provided for service call');
        servicePromise.reject(failure);
        throw failure;
      }

      // Ensure we have a product identifier
      //
      if (!payload.data?.order?.OrderID) {
        const failure = new Error('Missing order ID for request');
        servicePromise.reject(failure);
        throw failure;
      }

      // Bootstrapping the store to get the EvaEndpoint instance we need
      // The SDK caches bootstrap results so after the first call this will be instant
      //
      const endpoint = await bootstrapEndpoint({ uri: endpointUrl });

      // Set provided chain context header
      //
      const serviceNameForGetOrder = new Core.GetOrder().name;
      const serviceOptions: IEvaServiceCallOptions = Object.assign(
        {
          chainContext: payload.chainContext,
        },
        payload.serviceOptions,
      );

      // Set the current user authentication token if needed
      //
      const currentUserToken = storeState.currentUserToken;
      if (!serviceOptions.authenticationToken && currentUserToken) {
        serviceOptions.authenticationToken = currentUserToken;
      }

      // Set the current application token if needed
      //
      const currentApplicationToken = storeState.currentApplicationToken;
      if (!serviceOptions.applicationToken && currentApplicationToken) {
        serviceOptions.applicationToken = currentApplicationToken;
      }

      // Call the product details service and return the response so the reducers can
      // write it to the store state
      //
      logger.debug(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Call service ${serviceNameForGetOrder}`, payload, serviceOptions)();

      try {
        const order = await endpoint.callService(
          Core.GetOrder,
          {
            AdditionalOrderDataOptions: settings.currentOrderOptions,
            IncludeAllPayments: true,
            ShowOnlyShippableLines: false,
            ...payload.data.order,
          },
          serviceOptions,
        ) as EVA.Core.GetOrderResponse;

        const responsePayload: IEvaServiceResponseActionPayload<GetOrderWithStatus> = {
          serviceContext: payload.serviceContext,
          chainContext: payload.chainContext,
          data: {
            order,
            status: determineOrderStatus({
              requiredData: order.AdditionalOrderData?.RequiredData || {} as EVA.Core.RequiredData,
              productRequirements: order.AdditionalOrderData?.ProductRequirements ?? undefined,
              order: order.Result,
            }),
            productCount: countOrderProductsByType({ order: order.Result }),
          },
        };

        return responsePayload;
      } catch (error) {
        logger.error(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Service error`, error)();

        const errorPayload: IEvaServiceErrorActionPayload<GetOrderWithStatus> = {
          serviceContext: payload.serviceContext,
          chainContext: payload.chainContext,
          data: error.message,
        };

        // If this an HTTPError from fetch we can try to extract the JSON response
        //
        if (error instanceof ky.HTTPError) {
          try {
            const httpError: ky.HTTPError = error;
            const jsonError = await httpError.response.json();
            errorPayload.data = jsonError || error.message;
            errorPayload.httpResponse = error.response.status;
            const headers: Record<string, string> = {};
            error.response.headers.forEach((value, key) => (headers[key.toLowerCase()] = value));
            errorPayload.headers = headers;
          } catch (jsonParseError) {
            logger.warn(`[EVA:SDK:LOGIC:${serviceName}] Failed to parse JSON error response`, jsonParseError)();
          }
        }

        servicePromise.reject(errorPayload);
        throw errorPayload;
      }
    },
  });

  return [
    requestLogic,
  ];
}
